import * as React from 'react'
// libs
import { isDirty } from 'redux-form'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
// components
import { Modal } from 'components/UI'
import ScheduleReviewForm from './components/Form'
// hooks
import { useModalData, useShowModal } from 'hooks/index'
// constants
import {
  Forms,
  Modals,
  ENTITIES,
  REPEAT_OPTIONS,
  EACH_PERIOD_ENDS_TYPE_OPTIONS,
  PLAN_SCHEDULE_ENTITY_TYPE_VALUES
} from 'constants/index'
import { REVIEW_REPEAT_OPTIONS, REVIEW_REPEAT_VALUES, REVIEW_TYPE_OPTIONS } from 'constants/review'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// graphql
import { SCHEDULED_REVIEW_CREATE_MUTATION, SCHEDULED_REVIEW_UPDATE_MUTATION } from 'graphql/reviews/mutations'
// types
import {
  IUser,
  IPosition,
  IScheduledReview,
  IPlanScheduleDefault,
  IScheduledReviewResponse,
  IScheduledReviewersDateInput
} from 'types/graphql'
//utils
import { handleError } from 'utils/errors/handlers'
import { toFormValues } from 'components/Modals/PlanScheduleDefaultsModal'
import { get, isEmpty, isFunction } from 'lodash'
import { getRefetchQueriesForReviews } from 'utils/reviews'
import { generateUserName, getOptionByValue, getPlanFromEntity, getValueFromOption } from 'utils/index'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'auto',
    position: 'fixed' as any,
    maxWidth: '472px'
  }
}

type QueryResponse = {
  scheduledReviewCreate: IScheduledReviewResponse
}

const ENTITY_TYPE_TO_SCHEDULE_DEFAULT = {
  [ENTITIES.PLAN_OBJECTIVE]: PLAN_SCHEDULE_ENTITY_TYPE_VALUES.OBJECTIVE,
  [ENTITIES.PLAN_ACTION]: PLAN_SCHEDULE_ENTITY_TYPE_VALUES.ACTION,
  [ENTITIES.PLAN_KPI]: PLAN_SCHEDULE_ENTITY_TYPE_VALUES.KPI
}

const CreateScheduledReviewModal: React.FC = () => {
  const [createScheduledReview] = useMutation<QueryResponse>(SCHEDULED_REVIEW_CREATE_MUTATION)
  const [updateScheduledReview] = useMutation(SCHEDULED_REVIEW_UPDATE_MUTATION)

  const [isReviewNow, setIsReviewNow] = React.useState(false)

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  // redux
  const { isFormDirty } = useSelector((state) => ({
    isFormDirty: isDirty(Forms.SCHEDULE_REVIEW_FORM)(state)
  }))

  const {
    hideModal,
    modalData: { isOpen, isEdit, review, entity, levelId }
  } = useModalData({ modalType: Modals.SCHEDULE_REVIEW_MODAL })

  const { showModal: showReviewCreateModal } = useShowModal({ modalType: Modals.REVIEW_CREATE_MODAL })

  // constants
  const scheduledReview: IScheduledReview = get(review, 'scheduledReview')

  const plan = getPlanFromEntity(entity)

  // for a brand-new review, seed the form from the plan's matching schedule default
  const entityType = ENTITY_TYPE_TO_SCHEDULE_DEFAULT[entity?.entityType]

  const defaultValues = plan?.scheduleDefaults?.find(
    (scheduleDefault: IPlanScheduleDefault) =>
      scheduleDefault.entityType === entityType &&
      (entityType !== PLAN_SCHEDULE_ENTITY_TYPE_VALUES.OBJECTIVE || scheduleDefault.levelId === levelId)
  )

  const initialValues: IScheduledReview & any = scheduledReview
    ? {
        planId: scheduledReview.planId,
        repeating: scheduledReview.repeating,
        repeatEveryValue: scheduledReview.repeatEveryValue,
        repeatEveryType: getOptionByValue(scheduledReview.repeatEveryType, REPEAT_OPTIONS),
        periodEndsOn: getOptionByValue(scheduledReview.periodEndsOn, EACH_PERIOD_ENDS_TYPE_OPTIONS),
        startPeriod: scheduledReview.startPeriod,
        endPeriod: scheduledReview.endPeriod,
        completionDate: !scheduledReview.repeating && get(scheduledReview.scheduledReviewers, '[0].reviewDate.date'),
        recurrenceEnds: scheduledReview.endsOn,
        recurrenceEndsAt: scheduledReview.endsOnDate,

        end: getOptionByValue(get(scheduledReview, 'scheduledReviewers[0].reviewDate.end'), REVIEW_REPEAT_OPTIONS),
        period: getOptionByValue(get(scheduledReview, 'scheduledReviewers[0].reviewDate.period'), REVIEW_TYPE_OPTIONS),
        value: get(scheduledReview, 'scheduledReviewers[0].reviewDate.value'),

        singleReviewReviewers:
          !scheduledReview.repeating &&
          scheduledReview.scheduledReviewers.map(({ user, position }: IUser | (IPosition & any)) => {
            const isUser: boolean = isEmpty(position)

            return {
              reviewer: {
                value: isUser ? user.id : position.id,
                label: isUser ? generateUserName(user) : position.description,
                entity: isUser ? ENTITIES.USER : ENTITIES.POSITION
              }
            }
          }),
        repeatingReviewReviewers:
          scheduledReview.repeating &&
          scheduledReview.scheduledReviewers.map(({ user, position }: IUser | (IPosition & any)) => {
            const isUser: boolean = isEmpty(position)

            return {
              reviewer: {
                value: isUser ? user.id : position.id,
                label: isUser ? generateUserName(user) : position.description,
                entity: isUser ? ENTITIES.USER : ENTITIES.POSITION
              }
            }
          })
      }
    : toFormValues(defaultValues)

  // methods
  const handleSubmit = async (data) => {
    const input = {
      planId: entity.entityType === ENTITIES.PLAN ? entity.id : null,
      planObjectiveId: entity.entityType === ENTITIES.PLAN_OBJECTIVE ? entity.id : null,
      planActionId: entity.entityType === ENTITIES.PLAN_ACTION ? entity.id : null,
      planKPIId: entity.entityType === ENTITIES.PLAN_KPI ? entity.id : null,
      repeating: data.repeating,
      repeatEveryValue: parseFloat(data.repeatEveryValue),
      repeatEveryType: getValueFromOption(data.repeatEveryType),
      periodEndsOn: getValueFromOption(data.periodEndsOn),
      startPeriod: data.startPeriod,
      endPeriod: data.repeating ? get(data, 'firstPeriodEndsAt') : get(data, 'endPeriod'),
      endsOn: data.recurrenceEnds,
      endsOnDate: data.recurrenceEndsAt,
      scheduledReviewers: (!data.repeating ? data.singleReviewReviewers : data.repeatingReviewReviewers).map(
        ({ reviewer }) => {
          let reviewDate: IScheduledReviewersDateInput = {
            date: data.completionDate,
            end: getValueFromOption(data.end)
          }

          if (getValueFromOption(data.end) !== REVIEW_REPEAT_VALUES.END) {
            reviewDate = { ...reviewDate, value: parseFloat(data.value), period: getValueFromOption(data.period) }
          }

          if (reviewer.entity === ENTITIES.USER) {
            return {
              userId: getValueFromOption(reviewer),
              reviewDate
            }
          }

          if (reviewer.entity === ENTITIES.POSITION) {
            return {
              positionId: getValueFromOption(reviewer),
              reviewDate
            }
          }
        }
      )
    }

    const refetchQueries = getRefetchQueriesForReviews(entity)

    try {
      if (isFunction(treeQueryRefetch)) {
        await treeQueryRefetch()
      }

      if (isEdit) {
        if (isReviewNow && !isFormDirty) {
          showReviewCreateModal({ review, entity })
          hideModal()
          return
        }

        await updateScheduledReview({
          variables: {
            id: review.scheduledReview.id,
            input
          },
          refetchQueries
        })

        hideModal()
      } else {
        const {
          data: {
            scheduledReviewCreate: { data: scheduledReview }
          }
        } = await createScheduledReview({
          variables: { input },
          refetchQueries
        })

        if (isReviewNow) {
          showReviewCreateModal({
            review: {
              startPeriod: scheduledReview.startPeriod,
              endPeriod: scheduledReview.endPeriod,
              scheduledReview
            },
            entity
          })
        }
        hideModal()
      }
    } catch (error) {
      handleError(error, 'CreateScheduledReviewModal.handleSubmitOrUpdate')
    }
  }

  const handleSetIsReviewNow = (value) => setIsReviewNow(value)

  return (
    <Modal
      width={'xs'}
      isOpen={isOpen}
      disableBackdropClick
      disableEscapeKeyDown
      hideModal={hideModal}
      PaperProps={PAPER_PROPS}>
      <ScheduleReviewForm
        review={review}
        entity={entity}
        hideModal={hideModal}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        handleSetIsReviewNow={handleSetIsReviewNow}
      />
    </Modal>
  )
}

export default CreateScheduledReviewModal
